import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, ExternalLink, Radio } from "lucide-react";
import { API } from "../../App";
import { Button } from "../ui/button";
import { NOTES, SOJOURNS_URL, noteUrl, noteCard } from "../../content/sojournsNotes";

export const SOJOURNS_URLS = [SOJOURNS_URL, `${SOJOURNS_URL}/notes`, ...NOTES.map((n) => noteUrl(n.slug))];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : null);

// Admin: push the Sojourns URLs to search engines (IndexNow) and the Google checklist.
export default function SojournsSeoCard({ headers }) {
  const [last, setLast] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/seo/indexnow`, { headers }).then((r) => setLast(r.data.last || null)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ping = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/seo/indexnow`, { urls: SOJOURNS_URLS }, { headers });
      setLast({ value: r.data.at, ok: true, count: r.data.submitted, status_code: r.data.status_code });
      toast.success(`Sent ${r.data.submitted} URLs to search engines`);
      axios.post(`${API}/admin/seo/warm`, { urls: SOJOURNS_URLS }, { headers }).catch(() => {});
    } catch (e) {
      const detail = e?.response?.data?.detail || "IndexNow didn't answer";
      setLast((l) => ({ ...(l || {}), value: new Date().toISOString(), ok: false, detail }));
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SOJOURNS_URLS.join("\n"));
      toast.success("URLs copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 mb-6" data-testid="admin-sojourns-seo">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-medium text-[#2E2A26]">Search engines</h2>
          <p className="text-xs text-[#3D4035]/60 mt-1 max-w-xl">
            One tap tells Bing, DuckDuckGo, Yandex, Naver and Seznam about the {SOJOURNS_URLS.length} Sojourns pages straight away (IndexNow; Bing's index also feeds ChatGPT search and Copilot). Press it after every publish that changes these pages.
          </p>
        </div>
        <Button onClick={ping} disabled={sending} className="bg-[#2E2A26] hover:bg-black text-white rounded-full" data-testid="admin-seo-indexnow-btn">
          <Radio className="w-4 h-4 mr-2" /> {sending ? "Sending" : "Tell search engines now"}
        </Button>
      </div>
      <p className="text-xs mt-3" data-testid="admin-seo-indexnow-last">
        {last?.value ? (
          last.ok
            ? <span className="text-emerald-700">Last sent {fmt(last.value)} · {last.count} URLs accepted (HTTP {last.status_code}).</span>
            : <span className="text-red-700">Last attempt {fmt(last.value)} failed{last.detail ? `: ${last.detail}` : ` (HTTP ${last.status_code})`}</span>
        ) : <span className="text-[#3D4035]/50">Not sent yet.</span>}
      </p>

      <div className="grid md:grid-cols-2 gap-5 mt-5">
        <div>
          <h3 className="text-sm font-medium text-[#2E2A26] mb-2">Google (manual, two minutes)</h3>
          <ol className="text-xs text-[#3D4035]/70 space-y-1.5 list-decimal pl-4">
            <li>Open <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="text-[#C85A38] hover:underline inline-flex items-center gap-1">Search Console <ExternalLink className="w-3 h-3" /></a> for savicoliving.com.</li>
            <li>Paste each URL below into the inspection bar at the top, then press <b>Request indexing</b>. Google usually crawls within a day.</li>
            <li>Under Sitemaps, re-submit <code className="bg-stone-100 px-1">https://www.savicoliving.com/sitemap.xml</code> so the new pages are listed.</li>
            <li>In <a href="https://www.bing.com/webmasters" target="_blank" rel="noopener noreferrer" className="text-[#C85A38] hover:underline inline-flex items-center gap-1">Bing Webmaster Tools <ExternalLink className="w-3 h-3" /></a>, choose "Import from Google Search Console" once; the button above covers the rest.</li>
          </ol>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-[#2E2A26]">The pages</h3>
            <button type="button" onClick={copy} className="text-xs text-[#C85A38] hover:underline inline-flex items-center gap-1" data-testid="admin-seo-copy-urls">
              <Copy className="w-3 h-3" /> Copy all
            </button>
          </div>
          <ul className="text-xs text-[#3D4035]/70 space-y-1 break-all" data-testid="admin-seo-url-list">
            {SOJOURNS_URLS.map((u) => (
              <li key={u}><a href={u} target="_blank" rel="noopener noreferrer" className="hover:text-[#C85A38]">{u.replace("https://", "")}</a></li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-stone-100" data-testid="admin-seo-cards">
        <h3 className="text-sm font-medium text-[#2E2A26] mb-1">LinkedIn cards</h3>
        <p className="text-xs text-[#3D4035]/60 mb-3">One 1200×630 image per note, black and copper. Tap to open, then save it and attach it to the post with the note's link. The same image is what LinkedIn and WhatsApp show when the link is pasted.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {NOTES.map((n) => (
            <a key={n.slug} href={noteCard(n.slug).replace("https://www.savicoliving.com", "")} target="_blank" rel="noopener noreferrer" className="block group" data-testid={`admin-seo-card-${n.slug}`}>
              <img src={`/sojourn-media/notes/${n.slug}-card.jpg?v=2`} alt={n.title} width="1200" height="630" loading="lazy" className="w-full h-auto rounded-lg border border-stone-200 group-hover:opacity-90 transition-opacity" />
              <span className="block text-[11px] text-[#3D4035]/60 mt-1.5 leading-snug">{n.n} · {n.title}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
